import React from "react";
import { Col, Row, Typography, Collapse, Divider, Alert, Tag } from "antd";
import { QuestionCircleOutlined, UserOutlined, SafetyOutlined, LockOutlined } from "@ant-design/icons";

const { Title, Paragraph, Text } = Typography;
const { Panel } = Collapse;

const Account = () => {
  return (
    <Row type="flex">
      <Col span={20} offset={1}>
        <Title level={2}>Account Management Guide</Title>
        <Paragraph style={{ fontSize: '16px', marginBottom: '24px' }}>
          This guide covers account management, user roles, permissions, password management, 
          and how to manage team members within your vendor account.
        </Paragraph>

        <Divider />

        <Title level={4}>User Roles & Permissions</Title>
        <Paragraph style={{ marginBottom: '16px' }}>
          Different user roles have different permissions within the system:
        </Paragraph>

        <div style={{ marginBottom: '24px' }}>
          <Alert
            message={
              <span>
                <UserOutlined style={{ marginRight: '8px' }} />
                <Text strong>Admin</Text>
              </span>
            }
            description={
              <div>
                <Paragraph style={{ marginBottom: '8px' }}>
                  Admins have full access to all account features, including:
                </Paragraph>
                <ul style={{ marginBottom: 0 }}>
                  <li>Complete and edit vendor profile</li>
                  <li>Make payments</li>
                  <li>Attach, certify, and submit PQQs</li>
                  <li>Manage users (add, remove, change permissions)</li>
                  <li>View all applications and submissions</li>
                </ul>
              </div>
            }
            type="success"
            showIcon={false}
            style={{ marginBottom: '12px' }}
          />

          <Alert
            message={
              <span>
                <SafetyOutlined style={{ marginRight: '8px' }} />
                <Text strong>Approver</Text>
              </span>
            }
            description={
              <div>
                <Paragraph style={{ marginBottom: '8px' }}>
                  Approvers can perform most actions except user management:
                </Paragraph>
                <ul style={{ marginBottom: 0 }}>
                  <li>Complete and edit vendor profile</li>
                  <li>Make payments</li>
                  <li>Attach, certify, and submit PQQs</li>
                  <li>View applications and submissions</li>
                  <li><Text strong>Cannot:</Text> Manage other users</li>
                </ul>
              </div>
            }
            type="info"
            showIcon={false}
            style={{ marginBottom: '12px' }}
          />

          <Alert
            message={
              <span>
                <UserOutlined style={{ marginRight: '8px' }} />
                <Text strong>User</Text>
              </span>
            }
            description={
              <div>
                <Paragraph style={{ marginBottom: '8px' }}>
                  Regular users have limited permissions:
                </Paragraph>
                <ul style={{ marginBottom: 0 }}>
                  <li>Complete and edit vendor profile</li>
                  <li>Make payments</li>
                  <li>View applications and submissions</li>
                  <li><Text strong>Cannot:</Text> Certify or submit PQQs</li>
                  <li><Text strong>Cannot:</Text> Manage other users</li>
                </ul>
              </div>
            }
            type="warning"
            showIcon={false}
          />
        </div>

        <Divider />

        <Title level={4}>Frequently Asked Questions</Title>

        <Collapse defaultActiveKey={['1']} ghost>
          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                How do I change my password?
              </span>
            } 
            key="1"
          >
            <Paragraph>
              To change your password:
            </Paragraph>
            <ol>
              <li>Navigate to your account settings (usually accessible from your profile menu)</li>
              <li>Look for "Change Password" or "Security Settings"</li>
              <li>Enter your current password</li>
              <li>Enter your new password (follow password requirements)</li>
              <li>Confirm your new password</li>
              <li>Save changes</li>
            </ol>
            <Paragraph>
              <Text strong>Password Requirements:</Text> Your password should be strong and meet 
              security requirements (typically 8+ characters, including letters, numbers, and 
              special characters).
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                I forgot my password. How do I reset it?
              </span>
            } 
            key="2"
          >
            <Paragraph>
              If you've forgotten your password:
            </Paragraph>
            <ol>
              <li>Go to the login page</li>
              <li>Click "Forgot Password" or "Reset Password"</li>
              <li>Enter the email address associated with your account</li>
              <li>Check your email for a password reset link</li>
              <li>Click the link and follow the instructions to create a new password</li>
            </ol>
            <Paragraph>
              <Text strong>Note:</Text> The reset link is typically valid for a limited time 
              (usually 24 hours). If you don't receive the email, check your spam folder or 
              contact support.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                How do I add or remove team members?
              </span>
            } 
            key="3"
          >
            <Paragraph>
              <Text strong>Only Admin users can add or remove team members.</Text> To manage users:
            </Paragraph>
            <ol>
              <li>Navigate to the Users section (usually in Admin settings or navigation menu)</li>
              <li>Click "Add User" or "Invite User" to add a new team member</li>
              <li>Enter the new user's email address and assign their role (Admin, Approver, or User)</li>
              <li>Send the invitation</li>
              <li>The new user will receive an email with instructions to set up their account</li>
            </ol>
            <Paragraph>
              To remove a user, go to the Users section, find the user, and click "Remove" or 
              "Deactivate". You may need to confirm the action.
            </Paragraph>
            <Paragraph>
              <Text strong>Note:</Text> If you don't have Admin permissions but need to add users, 
              contact your account administrator or i4 Strategies support.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                How do I change a user's permissions or role?
              </span>
            } 
            key="4"
          >
            <Paragraph>
              <Text strong>Only Admin users can change user roles and permissions.</Text> To change 
              a user's role:
            </Paragraph>
            <ol>
              <li>Go to the Users section</li>
              <li>Find the user whose role you want to change</li>
              <li>Click "Edit" or the user's name</li>
              <li>Select the new role from the dropdown (Admin, Approver, or User)</li>
              <li>Save the changes</li>
            </ol>
            <Paragraph>
              <Text strong>Important:</Text> Changing a user's role may affect their ability to 
              perform certain actions. For example, if you change an Approver to a regular User, 
              they will no longer be able to certify or submit PQQs.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What's the difference between Admin, Approver, and User roles?
              </span>
            } 
            key="5"
          >
            <Paragraph>
              The key differences are:
            </Paragraph>
            <ul>
              <li>
                <Text strong>Admin:</Text> Full access including user management. Can do everything 
                an Approver can do, plus add/remove users and change permissions.
              </li>
              <li>
                <Text strong>Approver:</Text> Can complete profiles, make payments, and certify/submit 
                PQQs. Cannot manage other users.
              </li>
              <li>
                <Text strong>User:</Text> Can complete profiles and make payments, but cannot certify 
                or submit PQQs. Cannot manage other users.
              </li>
            </ul>
            <Paragraph>
              See the "User Roles & Permissions" section above for a detailed breakdown.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                How do I update my account information (email, name, etc.)?
              </span>
            } 
            key="6"
          >
            <Paragraph>
              To update your account information:
            </Paragraph>
            <ol>
              <li>Navigate to your account settings or profile page</li>
              <li>Find the section for personal information</li>
              <li>Update the fields you want to change (name, email, phone, etc.)</li>
              <li>Save your changes</li>
            </ol>
            <Paragraph>
              <Text strong>Note:</Text> If you change your email address, you may need to verify 
              the new email address. Check your inbox (and spam folder) for a verification email.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                Can I have multiple accounts for the same company?
              </span>
            } 
            key="7"
          >
            <Paragraph>
              Typically, each company should have one vendor account. However, you can have multiple 
              users within that account, each with their own login credentials. This is the recommended 
              approach because:
            </Paragraph>
            <ul>
              <li>All users share the same vendor profile and information</li>
              <li>Easier to manage and maintain consistency</li>
              <li>All submissions are associated with the same company account</li>
            </ul>
            <Paragraph>
              If you need multiple accounts for different business entities or subsidiaries, contact 
              i4 Strategies support to discuss your specific situation.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                How do I enable two-factor authentication (2FA)?
              </span>
            } 
            key="8"
          >
            <Paragraph>
              Two-factor authentication (2FA) adds an extra layer of security to your account. To enable it:
            </Paragraph>
            <ol>
              <li>Go to your account security settings</li>
              <li>Look for "Two-Factor Authentication" or "2FA"</li>
              <li>Follow the setup instructions (typically involves scanning a QR code with an 
              authenticator app)</li>
              <li>Save your backup codes in a secure location</li>
            </ol>
            <Paragraph>
              <Text strong>Note:</Text> 2FA availability may depend on your account type and system 
              configuration. If you don't see this option, contact support to inquire about enabling it.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                What should I do if I suspect unauthorized access to my account?
              </span>
            } 
            key="9"
          >
            <Paragraph>
              If you suspect unauthorized access:
            </Paragraph>
            <ol>
              <li><Text strong>Change your password immediately</Text></li>
              <li>Review your account activity and recent submissions</li>
              <li>Check if any unauthorized changes were made to your profile or user list</li>
              <li>Contact i4 Strategies support immediately at <Text strong>info@i4strategies.com</Text> 
              or <Text strong>212-909-9580</Text></li>
              <li>If you have 2FA enabled, review and revoke any suspicious sessions</li>
            </ol>
            <Paragraph>
              Security is a top priority. Report any suspicious activity as soon as possible.
            </Paragraph>
          </Panel>

          <Panel 
            header={
              <span>
                <QuestionCircleOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                How do I deactivate or delete my account?
              </span>
            } 
            key="10"
          >
            <Paragraph>
              Account deactivation or deletion should be handled through i4 Strategies support. 
              Contact <Text strong>info@i4strategies.com</Text> or <Text strong>212-909-9580</Text> 
              to discuss account closure.
            </Paragraph>
            <Paragraph>
              <Text strong>Important:</Text> Before deactivating your account, consider:
            </Paragraph>
            <ul>
              <li>Any active PQQ submissions or pending reviews</li>
              <li>Access to historical data and submissions</li>
              <li>Whether other team members need continued access</li>
            </ul>
            <Paragraph>
              Support can help you understand the implications and process for account closure.
            </Paragraph>
          </Panel>
        </Collapse>

        <Divider />

        <Title level={4}>Security Best Practices</Title>
        <ul style={{ paddingLeft: '20px' }}>
          <li>
            <LockOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            Use a strong, unique password that you don't use elsewhere
          </li>
          <li>
            <LockOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            Enable two-factor authentication if available
          </li>
          <li>
            <LockOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            Regularly review your account activity and user list
          </li>
          <li>
            <LockOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            Remove users who no longer need access
          </li>
          <li>
            <LockOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            Don't share your login credentials with others
          </li>
          <li>
            <LockOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            Log out when using shared or public computers
          </li>
        </ul>

        <Divider />

        <Title level={4}>Quick Reference: Who Can Do What?</Title>
        <div style={{ marginTop: '16px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
            <thead>
              <tr style={{ backgroundColor: '#f0f2f5' }}>
                <th style={{ padding: '12px', textAlign: 'left', border: '1px solid #d9d9d9' }}>Action</th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>
                  <Tag color="success">Admin</Tag>
                </th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>
                  <Tag color="processing">Approver</Tag>
                </th>
                <th style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>
                  <Tag color="warning">User</Tag>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td style={{ padding: '12px', border: '1px solid #d9d9d9' }}>Complete/Edit Profile</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✓</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✓</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✓</td>
              </tr>
              <tr>
                <td style={{ padding: '12px', border: '1px solid #d9d9d9' }}>Make Payment</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✓</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✓</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✓</td>
              </tr>
              <tr>
                <td style={{ padding: '12px', border: '1px solid #d9d9d9' }}>Certify/Submit PQQ</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✓</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✓</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✗</td>
              </tr>
              <tr>
                <td style={{ padding: '12px', border: '1px solid #d9d9d9' }}>Manage Users</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✓</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✗</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✗</td>
              </tr>
              <tr>
                <td style={{ padding: '12px', border: '1px solid #d9d9d9' }}>View Applications</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✓</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✓</td>
                <td style={{ padding: '12px', textAlign: 'center', border: '1px solid #d9d9d9' }}>✓</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{ marginTop: '32px', padding: '16px', backgroundColor: '#f0f2f5', borderRadius: '4px' }}>
          <Paragraph style={{ marginBottom: 0 }}>
            <Text strong>Need help with account management?</Text> Contact support at 
            <Text strong> info@i4strategies.com</Text> or <Text strong>212-909-9580</Text> for 
            assistance with user management, permissions, or account settings.
          </Paragraph>
        </div>
      </Col>
    </Row>
  );
};

export default Account;
